import { useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Petit hook utilitaire : remplace `window.confirm` par un AlertDialog shadcn élégant.
 * Usage:
 *   const { confirm, dialog } = useConfirm();
 *   const ok = await confirm({ title: "Supprimer ?", description: "..." });
 *   if (ok) doDestructiveAction();
 *   // dans le JSX: { dialog }
 */
export default function useConfirm() {
  const [state, setState] = useState({
    open: false,
    title: "Confirmer",
    description: "",
    confirmLabel: "Confirmer",
    cancelLabel: "Annuler",
    destructive: true,
    resolve: null,
  });

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || "Confirmer",
        description: opts.description || "Voulez-vous vraiment effectuer cette action ?",
        confirmLabel: opts.confirmLabel || "Confirmer",
        cancelLabel: opts.cancelLabel || "Annuler",
        destructive: opts.destructive !== false,
        resolve,
      });
    });
  }, []);

  const close = (value) => {
    state.resolve?.(value);
    setState((s) => ({ ...s, open: false, resolve: null }));
  };

  const dialog = (
    <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {state.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)} data-testid="confirm-cancel">
            {state.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => close(true)}
            data-testid="confirm-ok"
            className={state.destructive ? "bg-corten hover:bg-corten-dark text-cream" : ""}
          >
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, dialog };
}
